import type { Envelope, ErrDetail } from './types'

export const UNAUTHORIZED = 'domainstock:unauthorized'

// The CSRF token is derived server-side from the session and handed to us by
// /api/login and /api/me; it never touches localStorage.
let csrfToken = ''
export const setCsrf = (t: string) => {
  csrfToken = t
}
export const getCsrf = () => csrfToken

/**
 * newKey mints an Idempotency-Key. Callers must create it ONCE per user intent
 * (a click) and reuse the same key when the user presses "重试", so a lost
 * response can be replayed safely instead of writing twice.
 */
export function newKey(prefix = 'op'): string {
  const c = globalThis.crypto
  if (c && 'randomUUID' in c) return `${prefix}-${c.randomUUID()}`
  const rnd = Math.random().toString(36).slice(2) + Date.now().toString(36)
  return `${prefix}-${rnd}`
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: ErrDetail[]
  readonly requestId: string

  constructor(status: number, code: string, message: string, details: ErrDetail[], requestId: string) {
    super(message)
    this.status = status
    this.code = code
    this.details = details
    this.requestId = requestId
  }

  /** retryable means replaying the SAME idempotency key is safe and correct. */
  get retryable(): boolean {
    return this.status >= 500 || this.status === 429 || this.code === 'DB_BUSY' || this.code === 'NETWORK'
  }

  get conflictDetails(): ErrDetail[] {
    return this.details.filter((d) => d.domain || d.reason)
  }
}

interface Options {
  body?: unknown
  key?: string
  signal?: AbortSignal
}

async function request<T>(method: string, path: string, opts: Options = {}): Promise<T> {
  const headers: Record<string, string> = {}
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (opts.key) headers['Idempotency-Key'] = opts.key
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken

  let res: Response
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    })
  } catch (e) {
    // Chrome often reports an aborted fetch as "Failed to fetch" rather than
    // AbortError. A cancelled page load must not look like a lost write.
    if (opts.signal?.aborted || (e as Error)?.name === 'AbortError') {
      if ((e as Error)?.name === 'AbortError') throw e
      throw new DOMException('The operation was aborted.', 'AbortError')
    }
    // A dropped connection says nothing about the outcome of a write: the caller
    // must re-issue with the same key, which is why newKey() is per-intent.
    // A read has no result to lose.
    const message = opts.key
      ? '网络中断，结果未知；请使用同一请求重试以获取确定结果'
      : '没有连上服务器，请再试一次'
    throw new ApiError(0, 'NETWORK', message, [], '')
  }

  const text = await res.text()
  let env: Envelope<T> | null = null
  if (text) {
    try {
      env = JSON.parse(text) as Envelope<T>
    } catch {
      env = null
    }
  }
  if (res.status === 401 && !path.endsWith('/api/login')) {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED))
  }
  if (!res.ok || !env || env.code !== 'OK') {
    throw new ApiError(
      res.status,
      env?.code ?? 'INTERNAL',
      env?.message ?? (text ? text.slice(0, 200) : `HTTP ${res.status}`),
      env?.details ?? [],
      env?.requestId ?? '',
    )
  }
  return env.data as T
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, { signal }),
  post: <T>(path: string, body?: unknown, key?: string, signal?: AbortSignal) =>
    request<T>('POST', path, { body, key, signal }),
}

export function qs(params: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}
